import { useState, useRef, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Camera, Upload, FileText, CheckCircle2, AlertTriangle, Loader2, X, Ban, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const fmtR = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ScanInvoiceDialog = ({ open, onOpenChange, vehicles, suppliers, onSuccess }) => {
  const [step, setStep] = useState('upload'); // upload | scanning | review | confirming
  const [scanResult, setScanResult] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [duplicateCheck, setDuplicateCheck] = useState({ is_duplicate: false, duplicates: [] });
  const [duplicateOverride, setDuplicateOverride] = useState(false);
  const [lastVehicleOdo, setLastVehicleOdo] = useState(null);
  const [vehicleAvgKmL, setVehicleAvgKmL] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dupTimerRef = useRef(null);

  const resetState = () => {
    setStep('upload');
    setScanResult(null);
    setReviewData(null);
    setPreviewUrl(null);
    setDuplicateCheck({ is_duplicate: false, duplicates: [] });
    setDuplicateOverride(false);
    setLastVehicleOdo(null);
    setVehicleAvgKmL(null);
  };

  // Real-time duplicate checking for review data
  const runDuplicateCheck = useCallback(async (vehicleId, invoiceNumber, date, totalAmount) => {
    const hasInvoice = invoiceNumber && invoiceNumber.trim().length >= 2;
    const hasDateAmount = vehicleId && date && totalAmount && parseFloat(totalAmount) > 0;
    if (!hasInvoice && !hasDateAmount) {
      setDuplicateCheck({ is_duplicate: false, duplicates: [] });
      return;
    }
    try {
      const result = await api.checkDuplicate({
        vehicle_id: vehicleId || null,
        invoice_number: invoiceNumber?.trim() || null,
        date: date || null,
        amount: totalAmount ? parseFloat(totalAmount) : null,
      });
      setDuplicateCheck(result);
      setDuplicateOverride(false);
    } catch {
      setDuplicateCheck({ is_duplicate: false, duplicates: [] });
    }
  }, []);

  useEffect(() => {
    if (step !== 'review' || !reviewData) return;
    if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
    dupTimerRef.current = setTimeout(() => {
      runDuplicateCheck(reviewData.vehicle_id, reviewData.invoice_number, reviewData.date, reviewData.total_amount);
    }, 500);
    return () => { if (dupTimerRef.current) clearTimeout(dupTimerRef.current); };
    // We depend on the specific fields (not the whole reviewData object) to avoid
    // re-running the debounce whenever any unrelated field on reviewData changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, reviewData?.vehicle_id, reviewData?.invoice_number, reviewData?.date, reviewData?.total_amount, runDuplicateCheck]);

  // Fetch last odometer and avg km/L for selected vehicle
  useEffect(() => {
    const fetchLastOdo = async () => {
      const vid = reviewData?.vehicle_id;
      if (!vid || step !== 'review') { setLastVehicleOdo(null); setVehicleAvgKmL(null); return; }
      try {
        const costs = await api.getCosts({ vehicle_id: vid });
        if (costs.length > 0) {
          const sorted = costs.sort((a, b) => new Date(b.date) - new Date(a.date));
          const withOdo = sorted.find(c => c.odometer);
          setLastVehicleOdo(withOdo || null);
          // Calculate avg km/L from fuel entries
          const fuelCosts = sorted.filter(c => c.cost_type === 'fuel' && c.odometer && c.liters);
          let totalKm = 0, totalL = 0;
          for (let i = 0; i < fuelCosts.length - 1; i++) {
            const km = fuelCosts[i].odometer - fuelCosts[i + 1].odometer;
            if (km > 0) { totalKm += km; totalL += fuelCosts[i].liters; }
          }
          setVehicleAvgKmL(totalKm > 0 && totalL > 0 ? totalKm / totalL : null);
        } else {
          setLastVehicleOdo(null);
          setVehicleAvgKmL(null);
        }
      } catch {
        setLastVehicleOdo(null);
        setVehicleAvgKmL(null);
      }
    };
    fetchLastOdo();
  }, [reviewData?.vehicle_id, step]);

  const handleFile = async (file) => {
    if (!file) return;
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large. Maximum 10MB.');
      return;
    }

    // Show preview for images
    if (file.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }

    setStep('scanning');
    try {
      const result = await api.scanInvoice(file);
      setScanResult(result);
      setReviewData({
        scan_id: result.id,
        vehicle_id: result.matched_vehicle_id || '',
        date: result.date || '',
        liters: result.liters || '',
        price_per_liter: result.price_per_liter || '',
        total_amount: result.total_amount || '',
        invoice_number: result.invoice_number || '',
        odometer: result.vehicle_km || '',
        supplier_id: result.matched_supplier_id || '',
        vendor: result.supplier_name || '',
      });
      setStep('review');
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      // Budget / quota errors (HTTP 402) get a long-lived toast so the user can read it
      if (status === 402) {
        toast.error(typeof detail === 'string' ? detail : 'AI invoice scanning is temporarily unavailable.', { duration: 12000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to scan invoice. Try again.');
      }
      setStep('upload');
    }
  };

  const handleConfirm = async () => {
    if (!reviewData.vehicle_id) {
      toast.error('Please select a vehicle');
      return;
    }
    if (!reviewData.date || !reviewData.liters || !reviewData.total_amount) {
      toast.error('Date, Liters, and Total Amount are required');
      return;
    }
    if (duplicateCheck.is_duplicate && !duplicateOverride) {
      toast.error('Duplicate detected — check the override box to proceed.');
      return;
    }

    setStep('confirming');
    try {
      const payload = {
        scan_id: reviewData.scan_id,
        vehicle_id: reviewData.vehicle_id,
        date: reviewData.date,
        liters: parseFloat(reviewData.liters),
        price_per_liter: parseFloat(reviewData.price_per_liter),
        total_amount: parseFloat(reviewData.total_amount),
        invoice_number: reviewData.invoice_number || null,
        odometer: reviewData.odometer ? parseFloat(reviewData.odometer) : null,
        supplier_id: reviewData.supplier_id || null,
        vendor: reviewData.vendor || null,
      };
      await api.confirmInvoice(payload, duplicateCheck.is_duplicate && duplicateOverride);
      toast.success('Fuel cost entry created from invoice');
      onOpenChange(false);
      resetState();
      if (onSuccess) onSuccess();
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 409) {
        toast.error('Duplicate entry blocked. An entry with the same invoice number or date and amount already exists.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create cost entry');
      }
      setStep('review');
    }
  };

  const updateReview = (field, value) => {
    setReviewData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetState(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Scan Fuel Invoice
          </DialogTitle>
          <DialogDescription>
            Upload a photo or PDF of a fuel invoice to auto-extract cost data
          </DialogDescription>
        </DialogHeader>

        {/* STEP 1: Upload */}
        {step === 'upload' && (
          <div className="space-y-4 py-4">
            {/* Drop Zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
              onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
              onDrop={(e) => {
                e.preventDefault(); e.stopPropagation(); setIsDragging(false);
                const file = e.dataTransfer.files?.[0];
                if (file) handleFile(file);
              }}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center gap-3 p-10 border-2 border-dashed rounded-xl transition-all cursor-pointer ${
                isDragging
                  ? 'border-blue-500 bg-blue-50 scale-[1.02]'
                  : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/30'
              }`}
              data-testid="scan-drop-zone"
            >
              {isDragging ? (
                <>
                  <FileDown className="w-12 h-12 text-blue-500 animate-bounce" />
                  <p className="font-medium text-blue-700">Drop your invoice here</p>
                </>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-gray-400" />
                  <div className="text-center">
                    <p className="font-medium text-gray-700">Drag & drop invoice here</p>
                    <p className="text-sm text-gray-500 mt-1">or click to browse files</p>
                    <p className="text-xs text-gray-400 mt-2">PDF, JPG, PNG, WEBP — Max 10MB</p>
                  </div>
                </>
              )}
            </div>

            {/* Camera button */}
            <button
              onClick={() => cameraInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 p-3 border border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50/50 transition-all cursor-pointer group"
              data-testid="scan-camera-btn"
            >
              <Camera className="w-5 h-5 text-gray-400 group-hover:text-green-500 transition-colors" />
              <span className="text-sm font-medium text-gray-600 group-hover:text-green-700">Take Photo with Camera</span>
            </button>

            <input ref={fileInputRef} type="file" accept="image/*,.pdf" className="hidden"
              onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} data-testid="scan-file-input" />
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} data-testid="scan-camera-input" />

            <p className="text-xs text-center text-gray-400">
              Supported: OBARO fuel invoices. Max 10MB.
            </p>
          </div>
        )}

        {/* STEP 2: Scanning */}
        {step === 'scanning' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            {previewUrl && (
              <img src={previewUrl} alt="Invoice preview" className="max-h-48 rounded-lg border shadow-sm mb-2" />
            )}
            <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
            <p className="text-gray-600 font-medium">Scanning invoice...</p>
            <p className="text-xs text-gray-400">AI is extracting data from your invoice</p>
          </div>
        )}

        {/* STEP 3: Review */}
        {step === 'review' && reviewData && (
          <div className="space-y-4 py-2">
            {/* Confidence Badge */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {scanResult?.confidence === 'high' ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                )}
                <span className={`text-sm font-medium ${scanResult?.confidence === 'high' ? 'text-green-700' : 'text-amber-700'}`}>
                  {scanResult?.confidence === 'high' ? 'High confidence extraction' : 'Please verify extracted data'}
                </span>
              </div>
              {previewUrl && (
                <button onClick={() => window.open(previewUrl, '_blank')} className="text-xs text-blue-600 hover:underline">
                  View invoice
                </button>
              )}
            </div>

            {/* Vehicle Selection */}
            <div className="space-y-2">
              <Label>Vehicle *</Label>
              <Select value={reviewData.vehicle_id} onValueChange={(v) => updateReview('vehicle_id', v)}>
                <SelectTrigger data-testid="scan-vehicle-select">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name} ({v.registration})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {scanResult?.vehicle_registration && (
                <p className="text-xs text-gray-500">
                  Detected registration: <span className="font-mono font-bold">{scanResult.vehicle_registration}</span>
                  {scanResult.matched_vehicle_name && ` → matched to ${scanResult.matched_vehicle_name}`}
                </p>
              )}
            </div>

            {/* Date + Invoice Number */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" value={reviewData.date}
                  onChange={(e) => updateReview('date', e.target.value)}
                  data-testid="scan-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Invoice Number</Label>
                <Input value={reviewData.invoice_number}
                  onChange={(e) => updateReview('invoice_number', e.target.value)}
                  placeholder="INV-001" className="font-data"
                  data-testid="scan-invoice-input" />
              </div>
            </div>

            {/* Fuel Details */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-3">Fuel Details</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label className="text-blue-900 text-sm">Liters *</Label>
                  <Input type="number" step="any" value={reviewData.liters}
                    onChange={(e) => updateReview('liters', e.target.value)}
                    className="font-data border-blue-200 bg-white"
                    data-testid="scan-liters-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-blue-900 text-sm">Price/Liter (R) *</Label>
                  <Input type="number" step="0.01" value={reviewData.price_per_liter}
                    onChange={(e) => updateReview('price_per_liter', e.target.value)}
                    className="font-data border-blue-200 bg-white"
                    data-testid="scan-ppl-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-blue-900 text-sm">Total (R) *</Label>
                  <Input type="number" step="0.01" value={reviewData.total_amount}
                    onChange={(e) => updateReview('total_amount', e.target.value)}
                    className="font-data border-blue-200 bg-white font-bold"
                    data-testid="scan-total-input" />
                </div>
              </div>
              {/* Invoice Verification */}
              {reviewData.liters && reviewData.price_per_liter && reviewData.total_amount && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-700">Calculated:</span>
                    <span className="font-data font-medium">
                      {reviewData.liters}L x R{reviewData.price_per_liter} = {fmtR(parseFloat(reviewData.liters) * parseFloat(reviewData.price_per_liter))}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-700">Invoice Total:</span>
                    <span className="font-data font-bold">{fmtR(reviewData.total_amount)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Odometer */}
            <div className="space-y-2">
              <Label>Odometer Reading (km)</Label>
              <Input type="number" step="1" value={reviewData.odometer}
                onChange={(e) => updateReview('odometer', e.target.value)}
                placeholder="Vehicle km reading" className="font-data"
                data-testid="scan-odometer-input" />
              {lastVehicleOdo?.odometer && (
                <p className="text-xs text-red-600 font-medium" data-testid="scan-prev-odo">
                  Previous Odo: {lastVehicleOdo.odometer.toLocaleString()} km ({lastVehicleOdo.date})
                  {lastVehicleOdo.liters ? ` | ${lastVehicleOdo.liters}L` : ''}
                  {lastVehicleOdo.amount ? ` | R ${Number(lastVehicleOdo.amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : ''}
                </p>
              )}
              {lastVehicleOdo?.odometer && reviewData.odometer && parseFloat(reviewData.odometer) < lastVehicleOdo.odometer && (
                <div className="p-2 bg-red-100 border border-red-400 rounded text-xs text-red-800 font-bold" data-testid="scan-odo-warning">
                  Odometer {parseFloat(reviewData.odometer).toLocaleString()} km is LOWER than last recorded {lastVehicleOdo.odometer.toLocaleString()} km — please verify the reading is correct
                </div>
              )}
              {(() => {
                if (!lastVehicleOdo?.odometer || !reviewData.odometer || !vehicleAvgKmL) return null;
                const kmDriven = parseFloat(reviewData.odometer) - lastVehicleOdo.odometer;
                if (kmDriven <= 0) return null;
                const expected = kmDriven / vehicleAvgKmL;
                const actual = reviewData.liters ? parseFloat(reviewData.liters) : null;
                const diff = actual ? actual - expected : null;
                const diffPct = (actual && expected > 0) ? ((diff / expected) * 100) : null;
                const isWarning = diffPct !== null && diffPct > 20;
                return (
                  <div className={`mt-1 p-2 rounded text-xs font-medium ${isWarning ? 'bg-red-100 border border-red-300 text-red-800' : 'bg-slate-100 border border-slate-200 text-slate-700'}`} data-testid="scan-expected-litres">
                    <span>Avg expected: {expected.toFixed(1)}L for {kmDriven.toFixed(0)} km (avg {vehicleAvgKmL.toFixed(1)} km/L)</span>
                    {actual !== null && (
                      <span className="ml-1">
                        — Actual: {actual.toFixed(1)}L
                        {diffPct !== null && (
                          <span className={isWarning ? ' font-bold text-red-700' : ''}>
                            {' '}({diff > 0 ? '+' : ''}{diff.toFixed(1)}L / {diffPct > 0 ? '+' : ''}{diffPct.toFixed(0)}%)
                          </span>
                        )}
                      </span>
                    )}
                    {isWarning && (
                      <p className="mt-1 font-bold">Warning: Actual litres {diffPct.toFixed(0)}% above expected — check for fuel loss or theft</p>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Supplier */}
            <div className="space-y-2">
              <Label>Supplier</Label>
              {suppliers.length > 0 ? (
                <Select value={reviewData.supplier_id} onValueChange={(v) => updateReview('supplier_id', v)}>
                  <SelectTrigger data-testid="scan-supplier-select">
                    <SelectValue placeholder={reviewData.vendor || 'Select supplier'} />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={reviewData.vendor}
                  onChange={(e) => updateReview('vendor', e.target.value)}
                  placeholder="Supplier name" data-testid="scan-vendor-input" />
              )}
              {scanResult?.supplier_name && (
                <p className="text-xs text-gray-500">Detected: {scanResult.supplier_name}</p>
              )}
            </div>

            {/* Duplicate Warning Banner */}
            {duplicateCheck.is_duplicate && (
              <div className={`flex items-start gap-3 p-3 rounded-lg ${duplicateOverride ? 'bg-amber-50 border border-amber-300' : 'bg-red-50 border border-red-300'}`} data-testid="scan-duplicate-warning">
                <Ban className={`w-5 h-5 mt-0.5 shrink-0 ${duplicateOverride ? 'text-amber-600' : 'text-red-600'}`} />
                <div className="space-y-1 flex-1">
                  <p className={`text-sm font-semibold ${duplicateOverride ? 'text-amber-800' : 'text-red-800'}`}>
                    {duplicateOverride ? 'Duplicate override accepted' : 'Duplicate entry detected'}
                  </p>
                  {duplicateCheck.duplicates.map((dup, i) => (
                    <div key={dup.id || i} className={`text-xs rounded px-2 py-1 ${duplicateOverride ? 'text-amber-700 bg-amber-100' : 'text-red-700 bg-red-100'}`}>
                      {dup.cost_type?.toUpperCase()} | {dup.date} | R {Number(dup.amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                      {dup.invoice_number ? ` | Invoice: ${dup.invoice_number}` : ''}
                      {dup.vehicle_name ? ` | ${dup.vehicle_name}` : ''}
                    </div>
                  ))}
                  <label className="flex items-center gap-2 pt-1 cursor-pointer" data-testid="scan-duplicate-override-label">
                    <input
                      type="checkbox"
                      checked={duplicateOverride}
                      onChange={(e) => setDuplicateOverride(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 accent-amber-600"
                      data-testid="scan-duplicate-override-checkbox"
                    />
                    <span className={`text-xs font-medium ${duplicateOverride ? 'text-amber-800' : 'text-red-700'}`}>
                      I confirm this is not a duplicate — override and add anyway
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => { resetState(); }} data-testid="scan-rescan-btn">
                <X className="w-4 h-4 mr-1" /> Rescan
              </Button>
              <Button onClick={() => handleConfirm()} data-testid="scan-confirm-btn"
                className={duplicateCheck.is_duplicate && !duplicateOverride ? "bg-gray-400 cursor-not-allowed" : duplicateCheck.is_duplicate && duplicateOverride ? "bg-amber-600 hover:bg-amber-700" : "bg-green-600 hover:bg-green-700"}
                disabled={duplicateCheck.is_duplicate && !duplicateOverride}>
                {duplicateCheck.is_duplicate && !duplicateOverride ? (
                  <><Ban className="w-4 h-4 mr-1" /> Blocked — Duplicate</>
                ) : duplicateCheck.is_duplicate && duplicateOverride ? (
                  <><CheckCircle2 className="w-4 h-4 mr-1" /> Override & Create Entry</>
                ) : (
                  <><CheckCircle2 className="w-4 h-4 mr-1" /> Confirm & Create Entry</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4: Confirming */}
        {step === 'confirming' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="w-10 h-10 animate-spin text-green-500" />
            <p className="text-gray-600 font-medium">Creating cost entry...</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScanInvoiceDialog;
